import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { countriesApi, Country } from '@/api/countries.api';

interface CountrySelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (country: Country) => void;
  selectedCode?: string;
}

const CONTINENT_ORDER = [
  'All', 'Asia', 'Europe', 'North America', 'South America',
  'Middle East', 'Oceania', 'Africa', 'Central Asia',
];

// Fallback mapping used when DB continent is null (pre-migration)
const CONTINENT_FALLBACK: Record<string, string> = {
  'KR': 'Asia', 'JP': 'Asia', 'CN': 'Asia', 'TW': 'Asia', 'HK': 'Asia',
  'SG': 'Asia', 'MY': 'Asia', 'TH': 'Asia', 'VN': 'Asia', 'ID': 'Asia',
  'PH': 'Asia', 'IN': 'Asia', 'PK': 'Asia', 'BD': 'Asia', 'LK': 'Asia',
  'MM': 'Asia', 'KH': 'Asia', 'LA': 'Asia', 'MN': 'Asia', 'NP': 'Asia',
  'BT': 'Asia', 'MV': 'Asia', 'BN': 'Asia', 'TL': 'Asia',
  'AE': 'Middle East', 'SA': 'Middle East', 'QA': 'Middle East', 'KW': 'Middle East',
  'BH': 'Middle East', 'OM': 'Middle East', 'IL': 'Middle East', 'JO': 'Middle East',
  'LB': 'Middle East', 'SY': 'Middle East', 'IQ': 'Middle East', 'IR': 'Middle East',
  'YE': 'Middle East', 'TR': 'Middle East', 'PS': 'Middle East',
  'GB': 'Europe', 'FR': 'Europe', 'DE': 'Europe', 'IT': 'Europe', 'ES': 'Europe',
  'PT': 'Europe', 'NL': 'Europe', 'BE': 'Europe', 'CH': 'Europe', 'AT': 'Europe',
  'SE': 'Europe', 'NO': 'Europe', 'DK': 'Europe', 'FI': 'Europe', 'IE': 'Europe',
  'PL': 'Europe', 'CZ': 'Europe', 'HU': 'Europe', 'RO': 'Europe', 'BG': 'Europe',
  'GR': 'Europe', 'HR': 'Europe', 'SK': 'Europe', 'SI': 'Europe', 'LT': 'Europe',
  'LV': 'Europe', 'EE': 'Europe', 'LU': 'Europe', 'MT': 'Europe', 'CY': 'Europe',
  'IS': 'Europe', 'RU': 'Europe', 'UA': 'Europe', 'RS': 'Europe', 'XK': 'Europe',
  'US': 'North America', 'CA': 'North America', 'MX': 'North America',
  'GT': 'North America', 'SV': 'North America', 'CR': 'North America',
  'PA': 'North America', 'CU': 'North America', 'JM': 'North America',
  'HT': 'North America', 'DO': 'North America', 'PR': 'North America',
  'BR': 'South America', 'AR': 'South America', 'CL': 'South America',
  'CO': 'South America', 'PE': 'South America', 'VE': 'South America',
  'EC': 'South America', 'BO': 'South America', 'PY': 'South America',
  'UY': 'South America', 'GY': 'South America',
  'AU': 'Oceania', 'NZ': 'Oceania', 'FJ': 'Oceania', 'PG': 'Oceania',
  'ZA': 'Africa', 'EG': 'Africa', 'NG': 'Africa', 'KE': 'Africa', 'MA': 'Africa',
  'DZ': 'Africa', 'TN': 'Africa', 'GH': 'Africa', 'LY': 'Africa', 'LR': 'Africa',
  'CM': 'Africa', 'TZ': 'Africa', 'SN': 'Africa', 'MU': 'Africa',
  'KZ': 'Central Asia', 'UZ': 'Central Asia', 'AZ': 'Central Asia',
  'GE': 'Central Asia', 'AM': 'Central Asia',
};

/** Resolve continent: DB value first, then fallback mapping */
function getContinent(country: Country): string {
  return country.continent || CONTINENT_FALLBACK[country.code] || 'Other';
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const CountrySelectModal: React.FC<CountrySelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedCode,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContinent, setSelectedContinent] = useState('All');
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);

  // Fetch countries from API
  const { data: countriesData, isLoading } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await countriesApi.getAll();
      return response.data?.data || [];
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: isOpen,
  });

  const countries: Country[] = countriesData || [];

  // Build continent list: always show standard continents, append any extras from data
  const continents = useMemo(() => {
    const extras = new Set(countries.map(c => getContinent(c)));
    const result = [...CONTINENT_ORDER];
    extras.forEach(c => { if (!result.includes(c)) result.push(c); });
    return result;
  }, [countries]);

  // Filter countries based on search, continent, and letter
  const filteredCountries = useMemo(() => {
    return countries.filter(country => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (!country.name_en.toLowerCase().includes(search) &&
            !country.name_kr?.toLowerCase().includes(search) &&
            !country.code.toLowerCase().includes(search)) {
          return false;
        }
      }

      // Continent filter (DB value with fallback)
      if (selectedContinent !== 'All') {
        if (getContinent(country) !== selectedContinent) {
          return false;
        }
      }

      // Letter filter - check if letter is contained in name or code
      if (selectedLetter) {
        const upperLetter = selectedLetter.toUpperCase();
        if (!country.code.toUpperCase().includes(upperLetter)) {
          return false;
        }
      }

      return true;
    });
  }, [countries, searchTerm, selectedContinent, selectedLetter]);

  // Group countries by first letter for display
  const groupedCountries = useMemo(() => {
    const groups: Record<string, Country[]> = {};
    filteredCountries.forEach(country => {
      const letter = country.name_en[0].toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(country);
    });
    return groups;
  }, [filteredCountries]);

  // Reset filters when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedContinent('All');
      setSelectedLetter(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Select Country</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by name or code..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-np-500"
          />
        </div>

        {/* Continent Filter */}
        <div className="px-4 py-2 border-b overflow-x-auto min-h-14">
          <div className="flex gap-1 min-w-max">
            {continents.map(continent => (
              <button
                key={continent}
                onClick={() => { setSelectedContinent(continent); setSelectedLetter(null); }}
                className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  selectedContinent === continent
                    ? 'bg-np-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {continent}
              </button>
            ))}
          </div>
        </div>

        {/* Alphabet Filter */}
        <div className="px-4 py-2 border-b overflow-x-auto min-h-14">
          <div className="flex gap-0.5 min-w-max">
            <button
              onClick={() => setSelectedLetter(null)}
              className={`w-6 h-6 text-xs font-medium rounded transition-colors ${
                selectedLetter === null
                  ? 'bg-np-600 text-white'
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              All
            </button>
            {ALPHABET.map(letter => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                className={`w-6 h-6 text-xs font-medium rounded transition-colors ${
                  selectedLetter === letter
                    ? 'bg-np-600 text-white'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Country List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading countries...</div>
          ) : filteredCountries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No countries found</div>
          ) : (
            <div className="space-y-4">
              {Object.keys(groupedCountries).sort().map(letter => (
                <div key={letter}>
                  <div className="text-xs font-semibold text-gray-400 mb-2">{letter}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {groupedCountries[letter].map(country => (
                      <button
                        key={country.code}
                        onClick={() => { onSelect(country); onClose(); }}
                        className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors ${
                          selectedCode === country.code
                            ? 'bg-np-50 border-np-300 text-np-700'
                            : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                          {country.code}
                        </span>
                        <span className="text-sm truncate flex-1">
                          {country.name_en}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {filteredCountries.length} countries
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CountrySelectModal;
